/**
 * useKpiWrite — écritures du module KPI & Reporting.
 * Cibles :
 *   - ep:kpi-manual  : chiffres saisis à la main (campagnes / opérations / web).
 *                      Map { [id]: { ...entry } }. Chaque valeur tague source:"manuel".
 *   - ep:kpi-reports : historique des bilans générés. Array [{ ...report }].
 *
 * Même canal que les autres écritures V2 (doc/setDoc du shim). On repart toujours
 * de l'objet existant complet pour ne jamais détruire une entrée non éditée.
 */
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

const EP_KPI_MANUAL = "ep:kpi-manual";
const EP_KPI_REPORTS = "ep:kpi-reports";

function uid() {
  return String(Date.now()) + String(Math.random()).slice(2, 8);
}

/**
 * Crée ou met à jour une saisie manuelle.
 * @param {Object} current  map existante (depuis useKpiManual)
 * @param {Object} entry    { id?, scope, refId, clubId, month, platform?, values{}, note? }
 * @param {string|number} appId
 * @returns {Promise<string>} id de l'entrée
 */
export async function upsertManualEntry(current, entry, appId) {
  const map = current && typeof current === "object" ? current : {};
  const id = entry.id || uid();
  const now = new Date().toISOString();
  const prev = map[id] || {};
  const merged = {
    scope: "campaign",
    refId: null,
    clubId: null,
    month: null,
    platform: null,
    values: {},
    note: "",
    ...prev,
    ...entry,
    id,
    source: "manuel",
    createdAt: prev.createdAt || now,
    updatedAt: now,
    updatedBy: appId,
  };
  await setDoc(doc(db, "appdata", EP_KPI_MANUAL), {
    value: { ...map, [id]: merged },
    updatedAt: Date.now(),
  });
  return id;
}

/**
 * Supprime une saisie manuelle.
 */
export async function deleteManualEntry(current, id) {
  const map = current && typeof current === "object" ? { ...current } : {};
  delete map[id];
  await setDoc(doc(db, "appdata", EP_KPI_MANUAL), {
    value: map,
    updatedAt: Date.now(),
  });
}

/**
 * Enregistre un bilan dans l'historique.
 * @param {Array} current   liste existante (depuis useKpiReports)
 * @param {Object} report   { type, clubId?, period, title, comments?, snapshot }
 * @param {string|number} appId
 * @returns {Promise<string>} id du bilan
 */
export async function saveReport(current, report, appId) {
  const list = Array.isArray(current) ? current : [];
  const id = report.id || uid();
  const now = new Date().toISOString();
  const existingIdx = list.findIndex((r) => String(r.id) === String(id));
  const prev = existingIdx >= 0 ? list[existingIdx] : {};
  const saved = {
    type: "club",
    clubId: null,
    period: null,
    title: "",
    comments: "",
    snapshot: null,
    ...prev,
    ...report,
    id,
    createdAt: prev.createdAt || now,
    updatedAt: now,
    savedBy: appId,
  };
  const next = existingIdx >= 0
    ? list.map((r, i) => (i === existingIdx ? saved : r))
    : [saved, ...list];
  await setDoc(doc(db, "appdata", EP_KPI_REPORTS), {
    value: next,
    updatedAt: Date.now(),
  });
  return id;
}

/**
 * Supprime un bilan de l'historique.
 */
export async function deleteReport(current, id) {
  const list = Array.isArray(current) ? current : [];
  await setDoc(doc(db, "appdata", EP_KPI_REPORTS), {
    value: list.filter((r) => String(r.id) !== String(id)),
    updatedAt: Date.now(),
  });
}
